import React, { useState } from 'react';
import {
  ArticleHeading2,
  ArticleParagraph2,
  Button,
  IconTelegram16,
  IconWhatsapp16,
  Image,
  LinkButton,
  ModalWindow,
  TooltipDesktop,
} from '@cian/ui-kit';
import { useDeviceType } from '@cian/ui-kit/responsive';
import { useSelector } from 'react-redux';
import { TMeta } from '@cian/logger/shared';

import ClipBoardIcon from './assets/clipboard.svg';
import { ShareWithRealtorModalLayout } from '../../components/ShareWithRealtorModalLayout';
import { useApplicationContext } from '../../utils/applicationContext';
import { selectDealSaleB2bPromoUrl } from '../../selectors/links';
import { SHARE_LINK_TEXT } from '../CallToActionContainer';

export const ShareWithRealtorModalContainer: React.FC<{ open: boolean; onClose(): void }> = props => {
  const b2bPromoUrl = useSelector(selectDealSaleB2bPromoUrl);
  const [clipboardCopyTooltipMessage, setClipboardCopyTooltipMessage] = useState<string>();
  const { logger } = useApplicationContext();
  const onCopyLinkClick = async () => {
    const onError = (err: TMeta) => {
      setClipboardCopyTooltipMessage(`Не удалось скопировать ссылку: ${b2bPromoUrl}`);
      logger.error('ShareWithRealtorModalContainer: Failed to copy link to the clipboard', err);
    };

    if (window.navigator.clipboard) {
      await window.navigator.clipboard
        .writeText(b2bPromoUrl)
        .then(() => setClipboardCopyTooltipMessage('Ссылка скопирована'))
        .catch(onError);
    } else {
      onError({ message: 'No clipboard in navigator' });
    }

    const timeout = setTimeout(() => setClipboardCopyTooltipMessage(undefined), 2000);

    /* istanbul ignore next */

    return () => clearTimeout(timeout);
  };
  const isMobile = useDeviceType() === 'phone';
  const buttonsSize = isMobile ? 'M' : 'XS';

  return (
    <ModalWindow
      header={
        <ShareWithRealtorModalLayout.Header>
          <ArticleHeading2>Пригласите риелтора провести сделку</ArticleHeading2>
          <ArticleParagraph2 color="gray60_100">
            Отправьте своему риелтору ссылку на информацию о сервисе. По ссылке он сможет ознакомиться с деталями и
            заполнить заявку.
          </ArticleParagraph2>
        </ShareWithRealtorModalLayout.Header>
      }
      content={
        <ShareWithRealtorModalLayout.Content>
          <LinkButton
            size={buttonsSize}
            theme="fill_secondary"
            beforeIcon={<IconTelegram16 color="primary_100" />}
            href={`https://t.me/share/url?url=${b2bPromoUrl}&text=${encodeURI('\n')}${SHARE_LINK_TEXT}`}
            target="_blank"
            rel="noreferrer"
            fullWidth={isMobile}
          >
            Telegram
          </LinkButton>
          <LinkButton
            size={buttonsSize}
            theme="fill_secondary"
            beforeIcon={<IconWhatsapp16 color="primary_100" />}
            href={`https://wa.me/?text=${b2bPromoUrl}`}
            target="_blank"
            rel="noreferrer"
            fullWidth={isMobile}
          >
            WhatsApp
          </LinkButton>
          <TooltipDesktop
            open={Boolean(clipboardCopyTooltipMessage)}
            title={clipboardCopyTooltipMessage}
            placement="bottom"
            theme="black"
          >
            <Button
              fullWidth={isMobile}
              size={buttonsSize}
              onClick={onCopyLinkClick}
              theme="fill_secondary"
              beforeIcon={<Image width={16} height={16} src={ClipBoardIcon} color="primary_100" loading="lazy" />}
            >
              Скопировать ссылку
            </Button>
          </TooltipDesktop>
        </ShareWithRealtorModalLayout.Content>
      }
      {...props}
    />
  );
};
